import { Alert, Stack } from '@mui/material';
import React, { useState } from 'react';

import { initFilterParams } from '../../components/general';
import { ErrorAlert } from '../../components/general/ErrorAlert.tsx';
import { PlanningWarning } from '../../components/general/PlanningWarningDialog.tsx';
import { locationArt, ThemaFromThemaRouter } from '../../dtos.ts';
import { trpc } from '../../trpc.ts';
import { isPcCenterThema, isTermingruppe } from '../../utils';
import { content } from './CreateDurchgang.content.ts';
import { CreateDurchgangInput } from './CreateDurchgangInput/CreateDurchgangInput.tsx';
import { PlanningData } from './CreateDurchgangInput/planningData.types.ts';
import { CreateDurchgangResult } from './CreateDurchgangResult/CreateDurchgangResult.tsx';
import { SaveButtonState } from './DurchgangPlanung.types.ts';

type CreateDurchgangProps = Readonly<{
  thema: Pick<
    ThemaFromThemaRouter,
    'akaThemaId' | 'module' | 'planungsformat' | 'planungsinformation' | 'isOnline' | 'manuellerAusschreibungsprozess'
  >;
  setSaveButtonState: React.Dispatch<React.SetStateAction<SaveButtonState>>;
  onPlanningWarningsChange: React.Dispatch<React.SetStateAction<PlanningWarning[]>>;
  planningData: PlanningData;
  setPlanningData: React.Dispatch<React.SetStateAction<PlanningData>>;
  checkDurchgangAvailabilityQuery: ReturnType<typeof trpc.durchgang.checkDurchgangAvailability.useMutation>;
}>;

export const CreateDurchgang = ({
  thema,
  setSaveButtonState,
  onPlanningWarningsChange,
  planningData,
  setPlanningData,
  checkDurchgangAvailabilityQuery,
}: CreateDurchgangProps) => {
  const [useExpertenForAllDays, setUseExpertenForAllDays] = useState(false);
  const [filterParams, setFilterParams] = useState(() =>
    initFilterParams({
      raumSapIds: [],
      raumgroesse: thema.planungsinformation?.raumgroesse ?? null,
      locationArten: isPcCenterThema(thema.planungsinformation)
        ? [locationArt.HOTEL, locationArt.PC_CENTER]
        : [locationArt.HOTEL, locationArt.VERANSTALTUNGSLOCATION],
      freieTage: null,
      locationHatGruppenraum: thema.module.some((modul) => Boolean(modul.gruppenraumInfo)) || null,
      sitzordnung: thema.planungsinformation?.sitzordnung ?? null,
      teilnehmerErwartet: thema.planungsinformation?.teilnehmerErwartet ?? null,
    }),
  );

  if (!isTermingruppe(thema.planungsformat)) {
    return <Alert severity="warning">{content.wrongFormat}</Alert>;
  }

  if (!thema.planungsinformation?.planungszeitraum) {
    return <Alert severity="warning">{content.missingPlanningInfoZeitraum}</Alert>;
  }

  const expertePool = trpc.planung.resources.getBookableExpertenByThema.useQuery({
    akaThemaId: thema.akaThemaId,
    ...thema.planungsinformation.planungszeitraum,
  });

  return (
    <Stack spacing={3.5}>
      <CreateDurchgangInput
        thema={thema}
        expertePool={expertePool}
        useExpertenForAllDays={useExpertenForAllDays}
        setUseExpertenForAllDays={setUseExpertenForAllDays}
        checkDurchgangAvailabilityQuery={checkDurchgangAvailabilityQuery}
        planningData={planningData}
        setPlanningData={setPlanningData}
        setSaveButtonState={setSaveButtonState}
        filterParams={filterParams}
        setFilterParams={setFilterParams}
      />
      {checkDurchgangAvailabilityQuery.error && <ErrorAlert text={checkDurchgangAvailabilityQuery.error.message} />}
      {checkDurchgangAvailabilityQuery.data && (
        <CreateDurchgangResult
          thema={thema}
          expertePool={expertePool.data}
          setSaveButtonState={setSaveButtonState}
          onPlanningWarningsChange={onPlanningWarningsChange}
          availabilityResult={checkDurchgangAvailabilityQuery.data}
          filterParams={filterParams}
        />
      )}
    </Stack>
  );
};
